// Phase 10 — `useCountdown` hook (DECISIONS §E.2).
//
// Drives a per-task countdown dial. Given a task's wall-clock window
// (`startAtIso` → `endAtIso`, both immutable ISO instants from the
// anchored schedule) and the server-issued `clockOffset`, it returns
// the task duration, the seconds remaining until the end instant, the
// 1..0 fraction remaining, and whether the window has elapsed.
//
// Like `useElapsedSeconds`, it samples once per second — that is the
// granularity of the display — and recomputes from the corrected wall
// clock on every tick so it can never drift away from real time. The
// food does not pause, so neither does the dial.

import { useEffect, useState } from 'react';
import { correctedNow } from './helpers';

export interface Countdown {
  /** Whole task window in seconds (`end − start`). 0 if unparseable or non-positive. */
  durationSeconds: number;
  /** Seconds remaining until the end instant, clamped at 0. */
  remainingSeconds: number;
  /** Fraction of the window remaining, 1 (just started) → 0 (elapsed). */
  fraction: number;
  /** True once the end instant has passed. */
  expired: boolean;
}

function compute(startAtIso: string, endAtIso: string, clockOffset: number): Countdown {
  const startMs = Date.parse(startAtIso);
  const endMs = Date.parse(endAtIso);
  const nowMs = correctedNow(clockOffset);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return { durationSeconds: 0, remainingSeconds: 0, fraction: 0, expired: nowMs >= endMs };
  }
  const durationSeconds = Math.max(0, Math.round((endMs - startMs) / 1000));
  const remainingSeconds = Math.max(0, Math.ceil((endMs - nowMs) / 1000));
  const fraction =
    durationSeconds > 0 ? Math.max(0, Math.min(1, remainingSeconds / durationSeconds)) : 0;
  return { durationSeconds, remainingSeconds, fraction, expired: nowMs >= endMs };
}

export function useCountdown(startAtIso: string, endAtIso: string, clockOffset: number): Countdown {
  const [state, setState] = useState<Countdown>(() => compute(startAtIso, endAtIso, clockOffset));

  useEffect(() => {
    setState(compute(startAtIso, endAtIso, clockOffset));
    const id = setInterval(() => {
      setState(compute(startAtIso, endAtIso, clockOffset));
    }, 1000);
    return () => clearInterval(id);
  }, [startAtIso, endAtIso, clockOffset]);

  return state;
}
